import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
    BreadCrumbs,
    CommonModal as UpdateModal,
    CommonModal as DeleteModal,
    Input,
    PrimaryButton,
    SecondaryButton,
    PageTitleWithToolTip,
    AddButton,
    CircularLoading,
} from '../../Common';
import Routes from '../../../routes';
import { Grey1, Grey3 } from '../../../color_utils';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteConfirmation from '../../../Icons/img_delete_confirmation.svg';
import { exportType } from '../../Common/Table/utils';
import SystemManagerTable from './SystemManagerTable';
import UserInfo from '../../../UserInfo';

const SystemManagersPage = () => {
    const [modalData, setModalData] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [exportLoading, setExportLoading] = useState({ exportPdf: false, exportCsv: false });
    const [newSystemManagerError, setNewSystemManagerError] = useState(null);
    const exportFileRef = useRef();
    const canEdit = UserInfo.isSystemAdministrator() ||
        UserInfo.isNativeTenantAdministrator() ||
        UserInfo.isNativeRegionalAdministrator() ||
        UserInfo.isNativeSuperTenantAdministrator();

    const closeModal = (isDelete = false) => {
        if (isDelete) {
            return setModalData(prev => ({ ...prev, deleteOpen: false }));
        }
        setModalData(prev => ({ ...prev, editOpen: false }));
        setNewSystemManagerError(null);
    };

    const handleCreate = () => setModalData({
        editOpen: true,
        header: "Add System Manager",
        systemManagerName: "",
    });

    const resetState = () => {
        setModalData({});
        return setRefresh(!refresh);
    };

    const handleSuccess = (successType) => toast.success(`The System Manager was successfully ${successType}.`, { duration: 6000 });
    const handleError = () => toast.error('Something went wrong. Please try again.', { duration: 6000 });

    const handleEdit = row => {
        console.log({
            row
        })
        setModalData({
            editOpen: true,
            header: "Edit System Manager",
            systemManagerName: row.systemManager,
            id: row.id,
            enabled: row.enabled,
        })
    }

    const handleDelete = row => (
        setModalData({
            deleteOpen: true,
            systemManagerName: row.systemManager,
            id: row.id,
        })
    );

    const upsertSystemManager = async (id) => {
        if (!modalData.systemManagerName) {
            return setNewSystemManagerError("This field is required");
        }
        setNewSystemManagerError(null);

        await fetch(
            Routes.Account.SystemManagerUpsert(),
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id, name: modalData.systemManagerName })
            }
        )
            .then(res => {
                if (res.status === 200) {
                    handleSuccess(id > 0 ? 'edited' : 'added');
                } else {
                    toast.error('Error saving system manager. Please try again.', { duration: 6000 });
                }
            })
            .finally(() => resetState())
            .catch(err => handleError(err));
    };

    const deleteSystemManager = async (id) => {
        if (!id) 
            return;

        await fetch(
            Routes.Account.SystemManagerDelete(),
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id })
            }
        )
            .then(res => {
                if (res.status === 200) {
                    handleSuccess('deleted');
                } else {
                    toast.error('Error deleting system manager. Please try again.', { duration: 6000 });
                    console.log('Response', res);
                }
            })
            .finally(() => resetState())
            .catch(err => handleError(err));
    };

    const exportTableFile = async (e, type) => {
        setExportLoading({
            exportPdf: type === exportType.pdf,
            exportCsv: type === exportType.csv
        });

        if (exportFileRef.current) {
            exportFileRef.current.triggerExport(e, type);
        }

        setExportLoading({
            exportPdf: false,
            exportCsv: false,
        });
    };

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Account Details', to: `/Account` },
                    { text: 'Manage System Managers' }
                ]}
            />

            <Grid container>
                <Grid item xs={6}>
                    <PageTitleWithToolTip
                        pageTitle="Manage System Managers"
                        toolTipText="Manage System Managers of assets in the account. Use this custom field available to assign ownership of assets to individuals or organizations within your company portfolio."
                    />
                </Grid>
                <Grid item xs={6} align="right">
                    {canEdit && (
                        <AddButton
                            text="Add System Manager"
                            onClick={handleCreate}
                        />
                    )}
                </Grid>
                <Grid item xs={6} sx={{ marginTop: '1%' }}>
                    <SystemManagerTable
                        refresh={refresh}
                        exportFileRef={exportFileRef}
                        handleDelete={handleDelete}
                        handleEdit={handleEdit}
                    />
                </Grid>
            </Grid>

            <UpdateModal
                open={modalData.editOpen}
                close={() => closeModal()}
                header={modalData.header}
                headerTopMargin={10}
            >
                <Grid
                    container
                    spacing={2}
                    width={450}
                >
                    <Grid item xs={12} align="center" rowSpacing={12}>
                        <Input
                            required
                            sx={{ width: "84%" }}
                            variant="outlined"
                            label="System Manager Name"
                            error={newSystemManagerError}
                            value={modalData.systemManagerName}
                            onChange={e => setModalData(prev => ({ ...prev, systemManagerName: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} align="center" marginBottom={5}>
                        <PrimaryButton
                            useLargeButton
                            width={84}
                            buttonText={'Save'}
                            onClick={() => upsertSystemManager(modalData.id)}
                        />
                    </Grid>
                </Grid>
            </UpdateModal>

            <DeleteModal
                open={modalData.deleteOpen}
                close={() => closeModal(true)}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} align='center'>
                        <img
                            style={{ paddingTop: '1%', paddingBottom: '5%' }}
                            src={DeleteConfirmation}
                            width="275px"
                            height="250px"
                            alt="Are you sure you want to delete this system manager?"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="h5"
                            sx={{ '&.MuiTypography-root': { color: Grey1 } }}
                            align='center'
                        >
                            Delete this system manager ?
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="h5"
                            sx={{ '&.MuiTypography-root': { fontSize: 14, color: Grey3 } }}
                            align='center'
                        >
                            It will be permanently lost.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <PrimaryButton
                            useLargeButton
                            buttonText='Yes, Delete'
                            onClick={() => deleteSystemManager(modalData.id)}
                            width={75}
                        />
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <SecondaryButton
                            useLargeButton
                            buttonText='Cancel'
                            onClick={() => setModalData(prev => ({ ...prev, deleteOpen: false }))}
                            width={75}
                        />
                    </Grid>
                </Grid>
            </DeleteModal>
        </>
    );
};

export default SystemManagersPage;
